 
/* eslint-disable @typescript-eslint/no-unused-vars */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Company, CompanyDetails } from '../types/company';
import type { ResearchState, ResearchUnlock } from '../types/research';
import type { Employee, HiringProcess } from '../types/tycoon';
import { createSyncMiddleware, type WithSyncControls } from './middleware/syncMiddleware';
import { DataMigrationService } from '../services/DataMigrationService';
import type { DataVersion } from '../utils/dataMigration';
import { GAME_CONSTANTS } from '../config/gameConstants';
import { GAME_FORMULAS } from '../config/gameFormulas';
import { researchTree } from '../data/researchTree';
import { employeeService, EMPLOYEE_ROLES } from '../services/tycoon/employeeService';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';

export interface Financial {
  cash: number;
  revenue: number;
  costs: number;
  profit: number;
  dailyStats: {
    revenue: number;
    costs: number;
    profit: number;
    salesBreakdowns?: Array<{ productId: string; revenue: number; unitsSold: number }>;
  };
  monthlyStats: {
    revenue: number;
    costs: number;
    profit: number;
  };
  yearlyStats: {
    revenue: number;
    costs: number;
    profit: number;
  };
}

export interface CompanyMetrics {
  marketShare: number;
  reputation: number;
  innovationIndex: number;
  customerSatisfaction: number;
  brandStrength: number;
  competitivePosition: number;
}

export interface CompanyStore extends WithSyncControls {
  // State
  company: Company | null;
  companyDetails: CompanyDetails | null;
  research: ResearchState;
  finances: Financial;
  metrics: CompanyMetrics;
  employees: Employee[];
  hiringProcesses: HiringProcess[];
  
  // Actions
  updateCompany: (company: Partial<Company>) => void;
  updateCompanyDetails: (details: Partial<CompanyDetails>) => void;
  updateResearch: (research: Partial<ResearchState>) => void;
  updateFinances: (finances: Partial<Financial>) => void;
  updateMetrics: (metrics: Partial<CompanyMetrics>) => void;
  setEmployees: (employees: Employee[]) => void;
  addEmployee: (employee: Employee) => void;
  removeEmployee: (employeeId: string) => void;
  updateEmployee: (employeeId: string, updates: Partial<Employee>) => void;

  // Reputation management
  updateReputationFromSales: (dailySales: number, marketShare: number) => void;
  
  // Hiring process actions
  setHiringProcesses: (processes: HiringProcess[]) => void;
  addHiringProcess: (process: HiringProcess) => void;
  
  // Employee management actions
  startHiring: (params: {
    role: string;
    count: number;
    gameDate: { day: number; month: number; year: number };
    checkCapacity?: (role: string, count: number, hiringProcesses: HiringProcess[]) => {
      hasCapacity: boolean;
      availableCapacity: number;
      reason?: string;
    };
    onEvent?: (event: {
      type: string;
      title: string;
      description: string;
      impact: 'positive' | 'negative' | 'neutral';
    }) => void;
  }) => void;
  
  fireEmployees: (params: {
    role: string;
    count: number;
    onEvent?: (event: {
      type: string;
      title: string;
      description: string;
      impact: 'positive' | 'negative' | 'neutral';
    }) => void;
  }) => void;
  
  updateHiringProcesses: (params: {
    gameDate: { day: number; month: number; year: number };
    onEvent?: (event: {
      type: string;
      title: string;
      description: string;
      impact: 'positive' | 'negative' | 'neutral';
    }) => void;
  }) => void;
  
  updateEmployeeMetrics: () => void;
  
  // Research specific actions
  unlockResearchNode: (nodeId: string) => void;
  startResearch: (nodeId: string, startDate: { year: number; month: number; day: number }) => void;
  updateResearchProgress: (nodeId: string, progress: number) => void;
  completeResearch: (nodeId: string) => void;
  setResearchPoints: (points: number) => void;
  addResearchPoints: (points: number) => void;
  setMonthlyInvestment: (amount: number) => void;
  
  // Financial specific actions
  addRevenue: (amount: number) => void;
  addCost: (amount: number) => void;
  setCash: (amount: number) => void;
  spendFunds: (amount: number) => void;
  updateDailyStats: (stats: Partial<Financial['dailyStats']>) => void;
  
  // Reset
  resetCompany: () => void;
}

const initialResearchState: ResearchState = {
  unlockedNodes: [],
  activeResearch: [],
  researchPoints: 0,
  monthlyInvestment: 0,
  rpGenerationRate: 1,
};

const initialFinances: Financial = {
  cash: GAME_CONSTANTS.finance.initialCash,
  revenue: 0,
  costs: 0,
  profit: 0,
  dailyStats: {
    revenue: 0,
    costs: 0,
    profit: 0,
  },
  monthlyStats: {
    revenue: 0,
    costs: 0,
    profit: 0,
  },
  yearlyStats: {
    revenue: 0,
    costs: 0,
    profit: 0,
  },
};

// console.log(`[${new Date().toISOString()}] [CompanyStore] Store initialization - initialFinances.cash: ${initialFinances.cash}`);

const initialMetrics: CompanyMetrics = {
  marketShare: 0,
  reputation: 0,
  innovationIndex: 0,
  customerSatisfaction: 50,
  brandStrength: 0,
  competitivePosition: 0,
};

// Current version of the company store schema
const COMPANY_STORE_VERSION: DataVersion = { major: 1, minor: 0, patch: 0 };

// Custom sync configuration for company store
const companySyncConfig = {
  routing: {
    rules: [
      // Financial data is critical - sync immediately to IndexedDB
      {
        match: (path) => path === 'finances' || path.startsWith('finances.'),
        target: 'indexedDB' as const,
        priority: 100
      },
      // Company and employee data goes to IndexedDB
      {
        match: (path) => 
          path === 'company' || 
          path === 'companyDetails' || 
          path === 'employees' ||
          path.startsWith('employees.'),
        target: 'indexedDB' as const,
        priority: 90
      },
      // Research data can be batched
      {
        match: (path) => path === 'research' || path.startsWith('research.'),
        target: 'indexedDB' as const,
        priority: 70
      },
      // Metrics are updated frequently but not critical
      {
        match: (path) => path === 'metrics' || path.startsWith('metrics.'),
        target: 'localStorage' as const,
        priority: 60
      },
      // Default to IndexedDB for complex business data
      {
        match: () => true,
        target: 'indexedDB' as const,
        priority: 0
      }
    ],
    defaultTarget: 'indexedDB' as const
  },
  behavior: {
    debounceDelay: 0, // No debounce for financial data
    batchSize: 20, // Batch research updates
    enableAutoSync: true,
    syncOnReconnect: true,
    maxRetries: 5 // More retries for critical business data
  },
  hooks: {
    beforeSync: (data) => {
      const timestamp = new Date().toISOString();
      // console.log(`[${timestamp}] [CompanyStore.beforeSync] Called with data:`, {
      //   hasData: !!data,
      //   dataType: typeof data,
      //   currentCash: data?.finances?.cash,
      //   financesExists: !!data?.finances
      // });
      
      // Skip sync if data is null/undefined or being reset
      if (!data || typeof data !== 'object') {
        // console.debug(`[${timestamp}] [CompanyStore.beforeSync] Skipping sync - data is null/undefined`);
        return data;
      }
      
      // Ensure financial data integrity
      if (data.finances && typeof data.finances === 'object') {
        const finances = data.finances;
        
        // Sanitize all numeric values to prevent NaN
        const sanitizeNumber = (value: unknown): number => {
          if (typeof value !== 'number' || isNaN(value) || !isFinite(value)) {
            return 0;
          }
          return value;
        };
        
        // Sanitize base financial values
        // const timestamp2 = new Date().toISOString();
        const oldCash = finances.cash;
        finances.cash = sanitizeNumber(finances.cash);
        finances.revenue = sanitizeNumber(finances.revenue);
        finances.costs = sanitizeNumber(finances.costs);
        
        // if (oldCash !== finances.cash) {
        //   console.warn(`[${timestamp2}] [CompanyStore.beforeSync] CASH CHANGED during sanitization: ${oldCash} → ${finances.cash}`);
        //   console.trace('Cash change stack trace');
        // }
        
        // Recalculate profit to ensure consistency
        finances.profit = finances.revenue - finances.costs;
        
        // Ensure daily stats are sanitized and exist
        if (finances.dailyStats && typeof finances.dailyStats === 'object') {
          finances.dailyStats.revenue = sanitizeNumber(finances.dailyStats.revenue);
          finances.dailyStats.costs = sanitizeNumber(finances.dailyStats.costs);
          finances.dailyStats.profit = finances.dailyStats.revenue - finances.dailyStats.costs;
          
          // Ensure salesBreakdowns is always an array
          if (!Array.isArray(finances.dailyStats.salesBreakdowns)) {
            finances.dailyStats.salesBreakdowns = [];
          }
        } else {
          // Create default daily stats if missing
          finances.dailyStats = {
            revenue: 0,
            costs: 0,
            profit: 0,
            salesBreakdowns: []
          };
        }
        
        // Ensure monthly stats are sanitized
        if (finances.monthlyStats && typeof finances.monthlyStats === 'object') {
          finances.monthlyStats.revenue = sanitizeNumber(finances.monthlyStats.revenue);
          finances.monthlyStats.costs = sanitizeNumber(finances.monthlyStats.costs);
          finances.monthlyStats.profit = finances.monthlyStats.revenue - finances.monthlyStats.costs;
        } else {
          finances.monthlyStats = {
            revenue: 0,
            costs: 0,
            profit: 0
          };
        }
        
        // Ensure yearly stats are sanitized
        if (finances.yearlyStats && typeof finances.yearlyStats === 'object') {
          finances.yearlyStats.revenue = sanitizeNumber(finances.yearlyStats.revenue);
          finances.yearlyStats.costs = sanitizeNumber(finances.yearlyStats.costs);
          finances.yearlyStats.profit = finances.yearlyStats.revenue - finances.yearlyStats.costs;
        } else {
          finances.yearlyStats = {
            revenue: 0,
            costs: 0,
            profit: 0
          };
        }
      }
      return data;
    },
    afterSync: (result) => {
      if (result.success) {
        // console.debug('[CompanyStore] Synced successfully to', result.target);
      }
    },
    onError: (error) => {
      // Don't log errors for null/undefined data during reset
      if (error.message?.includes('null') || error.message?.includes('undefined')) {
        // console.debug('[CompanyStore] Sync skipped during reset:', error.item.path);
        return;
      }
      
      // console.error('[CompanyStore] Sync error:', error.message);
      // Critical error handling for financial data
      if (error.item.path.includes('finances')) {
        // console.error('[CompanyStore] CRITICAL: Failed to sync financial data', error);
      }
    }
  },
  monitoring: {
    trackMetrics: true,
    reportInterval: 30000, // Report metrics every 30 seconds
    metricsCallback: (metrics) => {
      // Monitor sync health for critical business data
      if (metrics.failedSyncs > 0) {
        // console.warn('[CompanyStore] Sync failures detected:', metrics.failedSyncs);
      }
    }
  }
};

// Initialize migration service for company store
const migrationService = DataMigrationService.getInstance();

// Register migration schemas
migrationService.registerMigrationSchemas('companyStore', [
  {
    version: { major: 1, minor: 0, patch: 0 },
    description: 'Initial company store schema',
    up: async (data) => data,
    down: async (data) => data
  }
]);

export const useCompanyStore = create<CompanyStore>()(
  devtools(
    createSyncMiddleware(
      'companyStore',
      companySyncConfig
    )(
      (set, get, api) => {
        // Define all store functions separately to ensure they're always available
        const storeDefinition = {
          // Initial state
          company: null,
          companyDetails: null,
          research: initialResearchState,
          finances: initialFinances,
          metrics: initialMetrics,
          employees: [],
          hiringProcesses: [],

          // Company actions
          updateCompany: (company) =>
            set(
              (state) => ({
                company: state.company 
                  ? { ...state.company, ...company } 
                  : { 
                      id: '',
                      name: '',
                      ceoName: '',
                      founded: { day: 1, month: 1, year: 2007 },
                      productCount: 0,
                      isPlayer: true,
                      ...company 
                    } as Company,
              }),
              false,
              'updateCompany'
            ),

      updateCompanyDetails: (details) =>
        set(
          (state) => ({
            companyDetails: state.companyDetails
              ? { ...state.companyDetails, ...details }
              : null,
          }),
          false,
          'updateCompanyDetails'
        ),

      // Research actions
      updateResearch: (research) =>
        set(
          (state) => ({
            research: { ...state.research, ...research },
          }),
          false,
          'updateResearch'
        ),

      unlockResearchNode: (nodeId) =>
        set(
          (state) => ({
            research: {
              ...state.research,
              unlockedNodes: [...state.research.unlockedNodes, nodeId],
            },
          }),
          false,
          'unlockResearchNode'
        ),

      startResearch: (nodeId, startDate) =>
        set(
          (state) => ({
            research: {
              ...state.research,
              activeResearch: [
                ...state.research.activeResearch,
                {
                  nodeId,
                  startDate,
                  progress: 0,
                  completed: false,
                },
              ],
            },
          }),
          false,
          'startResearch'
        ),

      updateResearchProgress: (nodeId, progress) =>
        set(
          (state) => ({
            research: {
              ...state.research,
              activeResearch: state.research.activeResearch.map((r) =>
                r.nodeId === nodeId ? { ...r, progress } : r
              ),
            },
          }),
          false,
          'updateResearchProgress'
        ),

      completeResearch: (nodeId) =>
        set(
          (state) => ({
            research: {
              ...state.research,
              activeResearch: state.research.activeResearch.map((r) =>
                r.nodeId === nodeId ? { ...r, progress: 100, completed: true } : r
              ),
              unlockedNodes: [...state.research.unlockedNodes, nodeId],
            },
          }),
          false,
          'completeResearch'
        ),

      setResearchPoints: (points) =>
        set(
          (state) => ({
            research: { ...state.research, researchPoints: points },
          }),
          false,
          'setResearchPoints'
        ),

      addResearchPoints: (points) =>
        set(
          (state) => ({
            research: {
              ...state.research,
              researchPoints: state.research.researchPoints + points,
            },
          }),
          false,
          'addResearchPoints'
        ),

      setMonthlyInvestment: (amount) =>
        set(
          (state) => ({
            research: { ...state.research, monthlyInvestment: amount },
          }),
          false,
          'setMonthlyInvestment'
        ),

      // Financial actions
      updateFinances: (finances) =>
        set(
          (state) => {
            // const timestamp = new Date().toISOString();
            // console.log(`[${timestamp}] [CompanyStore.updateFinances] Called with:`, {
            //   oldCash: state.finances?.cash,
            //   newCash: finances?.cash,
            //   financesKeys: Object.keys(finances || {})
            // });
            
            // Early return if finances is null/undefined
            if (!finances) {
              return state;
            }
            
            // Sanitize incoming financial data
            const sanitizeNumber = (value: unknown): number => {
              if (typeof value !== 'number' || isNaN(value) || !isFinite(value)) {
                return 0;
              }
              return value;
            };
            
            // Start with current state to ensure all nested properties exist
            const updatedFinances = { ...state.finances };
            
            // Update base financial values
            if (finances.cash !== undefined) updatedFinances.cash = sanitizeNumber(finances.cash);
            if (finances.revenue !== undefined) updatedFinances.revenue = sanitizeNumber(finances.revenue);
            if (finances.costs !== undefined) updatedFinances.costs = sanitizeNumber(finances.costs);
            if (finances.profit !== undefined) updatedFinances.profit = sanitizeNumber(finances.profit);
            
            // Update daily stats if provided
            if (finances.dailyStats) {
              updatedFinances.dailyStats = {
                ...state.finances.dailyStats,
                ...finances.dailyStats,
                salesBreakdowns: finances.dailyStats.salesBreakdowns || state.finances.dailyStats.salesBreakdowns || []
              };
            }
            
            // Update monthly stats if provided
            if (finances.monthlyStats) {
              updatedFinances.monthlyStats = {
                ...state.finances.monthlyStats,
                ...finances.monthlyStats
              };
            }
            
            // Update yearly stats if provided
            if (finances.yearlyStats) {
              updatedFinances.yearlyStats = {
                ...state.finances.yearlyStats,
                ...finances.yearlyStats
              };
            }
            
            return {
              finances: updatedFinances,
            };
          },
          false,
          'updateFinances'
        ),

      addRevenue: (amount) =>
        set(
          (state) => {
            const sanitizedAmount = (typeof amount === 'number' && !isNaN(amount) && isFinite(amount)) ? amount : 0;
            const newRevenue = state.finances.revenue + sanitizedAmount;
            return {
              finances: {
                ...state.finances,
                revenue: newRevenue,
                profit: newRevenue - state.finances.costs,
              },
            };
          },
          false,
          'addRevenue'
        ),

      addCost: (amount) =>
        set(
          (state) => {
            const sanitizedAmount = (typeof amount === 'number' && !isNaN(amount) && isFinite(amount)) ? amount : 0;
            const newCosts = state.finances.costs + sanitizedAmount;
            return {
              finances: {
                ...state.finances,
                costs: newCosts,
                profit: state.finances.revenue - newCosts,
              },
            };
          },
          false,
          'addCost'
        ),

      setCash: (amount) =>
        set(
          (state) => {
            const timestamp = new Date().toISOString();
            const oldCash = state.finances?.cash;
            const sanitizedAmount = (typeof amount === 'number' && !isNaN(amount) && isFinite(amount)) ? amount : 0;
            
            // console.log(`[${timestamp}] [CompanyStore.setCash] Called:`, {
            //   oldCash,
            //   newAmount: amount,
            //   sanitizedAmount,
            //   isValid: typeof amount === 'number' && !isNaN(amount) && isFinite(amount)
            // });
            
            if (sanitizedAmount === 0 && oldCash !== 0) {
              // console.error(`[${timestamp}] [CompanyStore.setCash] WARNING: Cash being set to 0!`);
              // console.trace('Stack trace for cash reset to 0');
            }
            
            return {
              finances: { 
                ...state.finances, 
                cash: sanitizedAmount,
                // Ensure dailyStats exists
                dailyStats: state.finances.dailyStats || {
                  revenue: 0,
                  costs: 0,
                  profit: 0,
                  salesBreakdowns: []
                }
              },
            };
          },
          false,
          'setCash'
        ),

      spendFunds: (amount) =>
        set(
          (state) => {
            const sanitizedAmount = (typeof amount === 'number' && !isNaN(amount) && isFinite(amount)) ? amount : 0;
            const newCash = Math.max(0, state.finances.cash - sanitizedAmount);
            const newCosts = state.finances.costs + sanitizedAmount;
            
            return {
              finances: {
                ...state.finances,
                cash: newCash,
                costs: newCosts,
                profit: state.finances.revenue - newCosts,
                // Ensure dailyStats exists
                dailyStats: state.finances.dailyStats || {
                  revenue: 0,
                  costs: 0,
                  profit: 0,
                  salesBreakdowns: []
                }
              },
            };
          },
          false,
          'spendFunds'
        ),

      updateDailyStats: (stats) =>
        set(
          (state) => ({
            finances: {
              ...state.finances,
              dailyStats: { ...state.finances.dailyStats, ...stats },
            },
          }),
          false,
          'updateDailyStats'
        ),

      // Metrics actions
      updateMetrics: (metrics) =>
        set(
          (state) => ({
            metrics: { ...state.metrics, ...metrics },
          }),
          false,
          'updateMetrics'
        ),

      // Reputation management
      updateReputationFromSales: (dailySales, marketShare) =>
        set(
          (state) => {
            const { calculateReputationGrowthRate } = GAME_FORMULAS.reputation;
            const currentReputation = state.metrics.reputation;

            // Calculate reputation growth from sales
            const reputationGain = calculateReputationGrowthRate(
              currentReputation,
              dailySales,
              marketShare
            );

            // Apply the gain
            const newReputation = Math.max(0, Math.min(100, currentReputation + reputationGain));

            return {
              metrics: {
                ...state.metrics,
                reputation: newReputation
              }
            };
          },
          false,
          'updateReputationFromSales'
        ),

      // Employee actions
      setEmployees: (employees) =>
        set(() => ({ employees }), false, 'setEmployees'),

      addEmployee: (employee) =>
        set(
          (state) => ({
            employees: [...state.employees, employee],
          }),
          false,
          'addEmployee'
        ),

      removeEmployee: (employeeId) =>
        set(
          (state) => ({
            employees: state.employees.filter((e) => e.id !== employeeId),
          }),
          false,
          'removeEmployee'
        ),

      updateEmployee: (employeeId, updates) =>
        set(
          (state) => ({
            employees: state.employees.map((e) =>
              e.id === employeeId ? { ...e, ...updates } : e
            ),
          }),
          false,
          'updateEmployee'
        ),

      // Hiring process actions
      setHiringProcesses: (processes) =>
        set(() => ({ hiringProcesses: processes }), false, 'setHiringProcesses'),

      addHiringProcess: (process) =>
        set(
          (state) => ({
            hiringProcesses: [...state.hiringProcesses, process],
          }),
          false,
          'addHiringProcess'
        ),

      // Employee management actions
      startHiring: ({ role, count, gameDate, checkCapacity, onEvent }) =>
        set((state) => {
          if (count <= 0) return state;

          const roleData = EMPLOYEE_ROLES[role];
          if (!roleData) return state;

          // Check building capacity if the function is provided
          if (checkCapacity) {
            const capacityCheck = checkCapacity(role, count, state.hiringProcesses);
            if (!capacityCheck.hasCapacity) {
              onEvent?.({
                type: 'building',
                title: 'Insufficient Capacity',
                description: capacityCheck.reason || `Not enough building capacity to hire ${count} ${roleData.name}(s)`,
                impact: 'negative'
              });
              return state;
            }
          }

          const hiringCost = roleData.baseSalary * 30 * count; // One month salary upfront

          if (state.finances.cash < hiringCost) {
            onEvent?.({
              type: 'financial',
              title: 'Insufficient Funds',
              description: `Not enough cash to hire ${count} ${roleData.name}(s). Need ${formatCurrency(hiringCost)}`,
              impact: 'negative'
            });
            return state;
          }

          // Create new hiring process
          const newProcess: HiringProcess = {
            role,
            count,
            startDate: { ...gameDate },
            daysRemaining: GAME_CONSTANTS.employee.hiringDays
          };

          onEvent?.({
            type: 'hr',
            title: 'Hiring Started',
            description: `Started hiring ${count} ${roleData.name}(s). They will join in ${GAME_CONSTANTS.employee.hiringDays} days. Cost: ${formatCurrency(hiringCost)}`,
            impact: 'neutral'
          });

          return {
            hiringProcesses: [...state.hiringProcesses, newProcess],
            finances: {
              ...state.finances,
              cash: state.finances.cash - hiringCost
            }
          };
        }, false, 'startHiring'),

      fireEmployees: ({ role, count, onEvent }) =>
        set((state) => {
          if (count <= 0) return state;

          const { remainingEmployees, firedEmployees } = employeeService.fireEmployeesByRole(
            state.employees,
            role,
            count
          );

          if (firedEmployees.length === 0) {
            onEvent?.({
              type: 'hr',
              title: 'No Employees to Fire',
              description: `No ${EMPLOYEE_ROLES[role]?.name}s available to fire`,
              impact: 'neutral'
            });
            return state;
          }

          const severanceCost = employeeService.calculateSeveranceCost(firedEmployees);
          const reputationImpact = employeeService.calculateReputationImpact(firedEmployees.length);

          if (state.finances.cash < severanceCost) {
            onEvent?.({
              type: 'financial',
              title: 'Insufficient Funds',
              description: `Not enough cash to pay severance. Need ${formatCurrency(severanceCost)}`,
              impact: 'negative'
            });
            return state;
          }

          onEvent?.({
            type: 'hr',
            title: 'Employees Terminated',
            description: `Fired ${firedEmployees.length} ${EMPLOYEE_ROLES[role]?.name}(s). Severance cost: ${formatCurrency(severanceCost)}, Reputation: -${reputationImpact.toFixed(1)}`,
            impact: 'negative'
          });

          console.log(`[FireEmployees] Reputation change: ${state.metrics.reputation} - ${reputationImpact} = ${Math.max(0, state.metrics.reputation - reputationImpact)}`);

          return {
            employees: remainingEmployees,
            finances: {
              ...state.finances,
              cash: state.finances.cash - severanceCost
            },
            metrics: {
              ...state.metrics,
              reputation: Math.max(0, state.metrics.reputation - reputationImpact)
            }
          };
        }, false, 'fireEmployees'),

      updateHiringProcesses: ({ gameDate, onEvent }) =>
        set((state) => {
          if (state.hiringProcesses.length === 0) return state;

          const { updatedProcesses, completedHires } = employeeService.updateHiringProcesses(
            state.hiringProcesses
          );

          if (completedHires.length === 0) {
            return { hiringProcesses: updatedProcesses };
          }

          const newEmployees = employeeService.createEmployeesFromHiring(completedHires);

          // Fire events for completed hires
          completedHires.forEach(hire => {
            onEvent?.({
              type: 'hr',
              title: 'Hiring Complete',
              description: `${hire.count} ${EMPLOYEE_ROLES[hire.role]?.name}(s) have joined the company`,
              impact: 'positive'
            });
          });

          return {
            employees: [...state.employees, ...newEmployees],
            hiringProcesses: updatedProcesses
          };
        }, false, 'updateHiringProcesses'),

      updateEmployeeMetrics: () =>
        set((state) => {
          const updatedEmployees = employeeService.updateEmployeeMetrics(state.employees);
          return { employees: updatedEmployees };
        }, false, 'updateEmployeeMetrics'),

      // Reset
      resetCompany: () => {
        // const timestamp = new Date().toISOString();
        // console.log(`[${timestamp}] [CompanyStore.resetCompany] Called`);
        // console.log(`[${timestamp}] [CompanyStore.resetCompany] Will set cash to: ${initialFinances.cash}`);
        
        return set(
          {
            company: null,
            companyDetails: null,
            research: { ...initialResearchState },
            finances: {
              ...initialFinances,
              dailyStats: {
                ...initialFinances.dailyStats,
                salesBreakdowns: []
              }
            },
            metrics: { ...initialMetrics },
            employees: [],
            hiringProcesses: [],
          },
          false,
          'resetCompany'
        );
      },
    };
    
    // Return the store definition to ensure functions are always present
    return storeDefinition;
  }
    ),
    {
      name: 'company-store',
      serialize: {
        options: {
          map: true,
          set: true,
          date: true,
          error: true,
          regex: true,
          undefined: true,
          nan: true,
          infinity: true,
          symbol: false
        }
      }
    }
  )
);

// Helper to persist company data immediately (for critical operations)
export const persistCompanyDataNow = () => {
  // This is a placeholder for future implementation
  // The sync middleware handles persistence automatically
  return Promise.resolve();
};

// Helper to load persisted company state
export const loadPersistedCompanyState = async () => {
  try {
    // Trigger migration if needed
    await migrationService.migrate('companyStore', COMPANY_STORE_VERSION, {
      createBackup: true,
      validateAfter: true
    });
    
    // Load persisted data
    const persistedData = await migrationService.getCurrentVersion('companyStore');
    return persistedData;
  } catch (error) {
    // console.error('[CompanyStore] Failed to load persisted state:', error);
    return null;
  }
};

// Helper to backup company data before major operations
export const backupCompanyData = async () => {
  try {
    const backupId = await migrationService.createBackup(
      'companyStore',
      COMPANY_STORE_VERSION,
      {
        compress: true,
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'manual_backup'
        }
      }
    );
    return backupId;
  } catch (error) {
    // console.error('[CompanyStore] Failed to create backup:', error);
    return null;
  }
};

// Selectors
export const selectCompany = (state: CompanyStore) => state.company;
export const selectCompanyDetails = (state: CompanyStore) => state.companyDetails;
export const selectResearch = (state: CompanyStore) => state.research;
export const selectFinances = (state: CompanyStore) => state.finances;
export const selectMetrics = (state: CompanyStore) => state.metrics;
export const selectEmployees = (state: CompanyStore) => state.employees;

// Computed selectors
export const selectCash = (state: CompanyStore) => state.finances.cash;
export const selectProfit = (state: CompanyStore) => state.finances.profit;
export const selectMarketShare = (state: CompanyStore) => state.metrics.marketShare;
export const selectReputation = (state: CompanyStore) => state.metrics.reputation;
export const selectResearchPoints = (state: CompanyStore) => state.research.researchPoints;

export const selectActiveResearchCount = (state: CompanyStore) =>
  state.research.activeResearch.filter((r) => !r.completed).length;

export const selectEmployeeCount = (state: CompanyStore) => state.employees.length;

export const selectEmployeesByRole = (state: CompanyStore) => {
  const byRole: Record<string, Employee[]> = {};
  state.employees.forEach((employee) => {
    if (!byRole[employee.role]) {
      byRole[employee.role] = [];
    }
    byRole[employee.role].push(employee);
  });
  return byRole;
};

export const selectTotalEmployeeCost = (state: CompanyStore) =>
  state.employees.reduce((total, employee) => total + employee.salary, 0);

// Hiring process selectors
export const selectHiringProcesses = (state: CompanyStore) => state.hiringProcesses;
export const selectActiveHiringCount = (state: CompanyStore) =>
  state.hiringProcesses.reduce((total, process) => total + process.count, 0);

// Employee management selectors
export const selectDailySalaryCost = (state: CompanyStore) =>
  employeeService.calculateDailySalaryCosts(state.employees);

export const selectProductivityMultiplier = (state: CompanyStore) =>
  employeeService.calculateProductivityMultiplier(state.employees);

export const selectEmployeeStats = (state: CompanyStore) =>
  employeeService.getEmployeeStats(state.employees);

// Research-related selectors

// Get all research nodes from the tree
export const selectAllResearchNodes = () => {
  if (!researchTree || !researchTree.branches) {
    return [];
  }
  return researchTree.branches.flatMap(branch => branch.nodes || []);
};

// Check if a node can be researched
export const selectCanResearchNode = (state: CompanyStore) => (nodeId: string) => {
  const allNodes = selectAllResearchNodes();
  const node = allNodes.find(n => n.id === nodeId);
  if (!node) return false;
  
  // Check if already unlocked
  if (state.research.unlockedNodes.includes(nodeId)) return false;
  
  // Check if already researching
  if (state.research.activeResearch.some(r => r.nodeId === nodeId)) return false;
  
  // Check prerequisites
  if (!node.prerequisites.every(prereq => state.research.unlockedNodes.includes(prereq))) return false;
  
  // Check RP cost
  if (state.research.researchPoints < node.cost) return false;
  
  // Check concurrent research limit
  const maxConcurrent = state.research.unlockedNodes.includes('business_innovation_lab') ? 3 : GAME_CONSTANTS.research.maxConcurrentResearch;
  if (state.research.activeResearch.length >= maxConcurrent) return false;
  
  return true;
};

// Get all active unlocks from unlocked nodes
export const selectActiveUnlocks = (state: CompanyStore) => {
  const allNodes = selectAllResearchNodes();
  const unlocks: ResearchUnlock[] = [];
  
  state.research.unlockedNodes.forEach(nodeId => {
    const node = allNodes.find(n => n.id === nodeId);
    if (node) {
      unlocks.push(...node.unlocks);
    }
  });
  
  return unlocks;
};

// Get stat improvements from research
export const selectStatImprovements = (state: CompanyStore) => {
  const improvements: Record<string, number> = {};
  const unlocks = selectActiveUnlocks(state);
  
  unlocks.forEach(unlock => {
    if (unlock.type === 'stat_improvement' && typeof unlock.value === 'number') {
      improvements[unlock.id] = (improvements[unlock.id] || 0) + unlock.value;
    }
  });
  
  return improvements;
};

// Get available nodes for research
export const selectAvailableNodes = (state: CompanyStore) => {
  const allNodes = selectAllResearchNodes();
  return allNodes.filter(node => {
    // Not yet unlocked
    if (state.research.unlockedNodes.includes(node.id)) return false;
    
    // Prerequisites met
    return node.prerequisites.every(prereq => state.research.unlockedNodes.includes(prereq));
  });
};

// Get nodes by category
export const selectNodesByCategory = (category: 'technology' | 'design' | 'business') => {
  const branch = researchTree.branches.find(b => b.id === category);
  return branch ? branch.nodes : [];
};

// Calculate daily RP from investment
export const selectDailyRP = (state: CompanyStore) => {
  const baseRP = state.research.monthlyInvestment * GAME_CONSTANTS.research.baseRPRate / 30;
  return baseRP * state.research.rpGenerationRate;
};

// Calculate product-based daily RP
export const selectProductBasedDailyRP = () => (reputation: number, activeProductCount: number) => {
  if (activeProductCount === 0) return 0;
  return Math.floor((reputation / 10) * activeProductCount);
};

// Calculate total daily RP generation
export const selectTotalDailyRP = (state: CompanyStore) => (reputation: number, activeProductCount: number) => {
  const investmentRP = selectDailyRP(state);
  const productRP = selectProductBasedDailyRP()(reputation, activeProductCount);
  return investmentRP + productRP;
};